import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePostLoginRedirect } from "@/hooks/usePostLoginRedirect";
import { usePWABanner } from "@/hooks/usePWABanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import PWAInstallBanner from "@/components/mobile/PWAInstallBanner";
import { cn } from "@/lib/utils";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const { isBannerVisible } = usePWABanner();

  // Use the post-login redirect hook
  const { isCheckingPending } = usePostLoginRedirect({ redirectUrl });

  // Check if this is a fresh redirect (vs manual navigation to login)
  useEffect(() => {
    if (redirectUrl) {
      // Only show redirect message if user arrived here recently
      // We'll show it initially, but users can dismiss it or it fades after manual navigation
      const isDirectNavigation =
        document.referrer === "" ||
        document.referrer.includes("/login") ||
        !document.referrer.includes(window.location.origin);

      setShowRedirectMessage(!isDirectNavigation);
    }
  }, [redirectUrl]);

  // Clear redirect message after some time or if user starts typing
  useEffect(() => {
    if (showRedirectMessage) {
      const timer = setTimeout(() => {
        setShowRedirectMessage(false);
      }, 10000); // Hide after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [showRedirectMessage]);

  // Clear redirect message when user starts interacting
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (showRedirectMessage) {
      setShowRedirectMessage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const success = await login(email, password);
      if (success) {
        toast({
          title: "Logged in successfully",
          description: redirectUrl
            ? "Redirecting to your requested page..."
            : "Welcome back!",
        });
        // Redirect will be handled by usePostLoginRedirect hook
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Login failed",
        description: "Please check your credentials and try again",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {/* PWA Install Banner */}
      <PWAInstallBanner />

      <div
        className={cn(
          "min-h-screen flex items-center justify-center p-4 relative overflow-hidden",
          isBannerVisible ? "pt-20" : "pt-4"
        )}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 h-full w-full bg-background bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] opacity-50"></div>

        {/* Gradient Blob */}
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-secondary/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-accent/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

        <div className="w-full max-w-md relative z-10">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 flex items-center justify-center">
                <img
                  src="/logo.svg"
                  alt="Logo"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Welcome Back
            </h1>
            <p className="mt-2 text-muted-foreground">
              Sign in to manage your permit submissions
            </p>
          </div>

          <Card className="shadow-sophisticated highlight-top border-t border-t-white/20 backdrop-blur-sm bg-card/95">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl">Sign In</CardTitle>
              <CardDescription>
                {showRedirectMessage && redirectUrl
                  ? "Please sign in to continue to your requested page"
                  : "Enter your credentials to access your account"}
              </CardDescription>
            </CardHeader>

            {showRedirectMessage && redirectUrl && (
              <div className="mx-6 mb-4 p-3 bg-blue-50/50 border border-blue-200/50 rounded-md backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <p className="text-blue-800 text-sm">
                    🔗 You were redirected here to sign in.
                  </p>
                  <button
                    onClick={() => setShowRedirectMessage(false)}
                    className="text-blue-600 hover:text-blue-800 text-sm ml-2"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-foreground/80"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={handleEmailChange}
                    required
                    className="h-11 shadow-inner bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label
                      htmlFor="password"
                      className="text-sm font-medium text-foreground/80"
                    >
                      Password
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs font-medium text-primary hover:text-primary/80 hover:underline transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 shadow-inner bg-background/50"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 btn-gradient shadow-md text-base font-medium mt-2 transition-all hover:scale-[1.02]"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Login;
